import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { formatQhnum } from "../plan/tasks_hub/taskHubUtils";

// A slim day strip: one row per task, one bar per occurrence, placed on the
// store's business-day axis with the "now" line across every row. Both Live
// Action modes render it above their Now list as the shape of the whole day
// at a glance; clicking a bar opens that occurrence.

// A bar's colour is the occurrence's status, in the same grammar as the
// status pills (OCC_STATUS_PILL): slate = not started, blue = acknowledged,
// violet = in progress, amber = waiting for validation, emerald = completed.
// Timing is the bar's position against the now line, not its colour; the one
// exception is a window that closed with the work not done, which is rose
// like a missed occurrence in Reports.
const BAR_STATES = {
  new: { cls: "bg-slate-200 hover:bg-slate-300 border border-slate-300", label: "Not started" },
  acknowledged: { cls: "bg-blue-500 hover:bg-blue-600", label: "Acknowledged" },
  in_progress: { cls: "bg-violet-500 hover:bg-violet-600", label: "In progress" },
  waiting_validation: { cls: "bg-amber-500 hover:bg-amber-600", label: "Waiting for validation" },
  completed: { cls: "bg-emerald-500 hover:bg-emerald-600", label: "Completed" },
  overdue: { cls: "bg-rose-500 hover:bg-rose-600", label: "Overdue" },
};
// Acknowledged is kept in BAR_STATES so a bar in that state still gets a
// colour, but it is out of the legend: nothing sets it today.
const LEGEND = ["new", "in_progress", "waiting_validation", "completed", "overdue"];

export function buildMapEntries(planTasks, nowQh) {
  const list = [];
  (planTasks || []).forEach((task) => {
    const interval = task.intraday_repeat_interval_minutes
      ? task.intraday_repeat_interval_minutes / 60
      : null;
    (task.plan_task_assignments || []).forEach((a) => {
      if (!a.is_assigned) return;
      let lo = task.lower_bound_qhnum;
      let hi = task.upper_bound_qhnum;
      if (task.is_intraday && interval && a.intraday_slot_index != null) {
        lo = task.lower_bound_qhnum + a.intraday_slot_index * interval;
        hi = Math.min(lo + interval, task.upper_bound_qhnum);
      }
      const isAnytime = lo == null || hi == null;
      // Status first (older payloads without lifecycle_status fall back to
      // the legacy status field), then the timing text for the tooltip.
      let state = a.lifecycle_status;
      if (!state) {
        if (!!a.submitted_at && !!a.requires_validation && !a.validated_at) state = "waiting_validation";
        else if (a.status === "completed" || a.status === "completed_late") state = "completed";
        else if (a.status === "in_progress") state = "in_progress";
        else state = "new";
      }
      const open = state !== "completed" && state !== "waiting_validation";
      if (open && a.status === "overdue") state = "overdue";
      let whenText;
      if (state === "completed") {
        whenText = a.submitted_at_formatted ? `Done at ${a.submitted_at_formatted}` : "Done";
      } else if (state === "waiting_validation") {
        whenText = a.submitted_at_formatted ? `Submitted at ${a.submitted_at_formatted}` : "Submitted";
      } else if (state === "overdue") {
        whenText = isAnytime ? "Was due today" : `Was due by ${formatQhnum(hi)}`;
      } else if (isAnytime) {
        whenText = "Anytime today";
      } else if (lo > nowQh) {
        whenText = `Opens at ${formatQhnum(lo)}`;
      } else if (hi > nowQh) {
        whenText = `Open until ${formatQhnum(hi)}`;
      } else {
        whenText = `${formatQhnum(lo)} to ${formatQhnum(hi)}`;
      }
      list.push({
        key: a.uuid,
        task,
        a,
        lo,
        hi,
        state,
        whenText,
        slotLabel:
          task.is_intraday && a.intraday_slot_index != null && lo != null ? formatQhnum(lo) : null,
      });
    });
  });
  return list;
}

// The strip's time axis. Tasks are the subject, so the axis is the span of
// the day's task windows, widened to the shifts on the labor chart and to the
// now line, and rounded out to whole hours. (Borrowing the labor chart's
// shift-only bounds squeezed the whole day into one hour on a day with a
// single short clock-in.) Anytime tasks have no window and do not count; a
// day with nothing timed shows a plain business day.
const FALLBACK_AXIS = { start: 8, end: 22 };

export function axisBoundsFor(entries, shifts, nowQh) {
  const values = [];
  (entries || []).forEach((e) => {
    if (e.lo != null && e.hi != null) values.push(Number(e.lo), Number(e.hi));
  });
  (shifts || []).forEach((d) => {
    const start = Number(d.shift_start);
    let end = Number(d.shift_end);
    if (start === 0 && end === 0) return;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;
    if (end < start) end += 24;
    values.push(start, end);
  });
  const timed = values.filter((v) => Number.isFinite(v));
  if (timed.length === 0) return { ...FALLBACK_AXIS };
  if (Number.isFinite(nowQh)) timed.push(nowQh);
  const start = Math.floor(Math.min(...timed));
  const end = Math.max(Math.ceil(Math.max(...timed)), start + 1);
  return { start, end };
}

/**
 * The bars that are one person's to do: the entries whose occurrence is on
 * their Now list, whether it can be done now, opens later today or is already
 * done. Everything else on the store's day belongs to somebody else, and a
 * kiosk screen for one person has no use for it.
 */
export function entriesForPerson(entries, nowData) {
  const keys = new Set(
    [
      ...(nowData?.tasks || []),
      ...(nowData?.upcoming_today || []),
      ...(nowData?.done_today || []),
    ]
      .map((row) => row?.plan_task_assignment_uuid)
      .filter(Boolean)
  );
  return (entries || []).filter((entry) => keys.has(entry.key));
}

export function DayGanttMap({ entries, axisStart, axisEnd, nowQh, onBarClick }) {
  const span = Math.max(axisEnd - axisStart, 1);
  const pct = (qh) =>
    Math.min(Math.max(((qh - axisStart) / span) * 100, 0), 100);

  // One row per task, ordered by start time; anytime tasks sink to the bottom.
  const rows = [];
  const rowByUuid = {};
  [...entries]
    .sort((a, b) => (a.lo ?? 99) - (b.lo ?? 99))
    .forEach((e) => {
      if (!rowByUuid[e.task.uuid]) {
        rowByUuid[e.task.uuid] = { task: e.task, segments: [] };
        rows.push(rowByUuid[e.task.uuid]);
      }
      rowByUuid[e.task.uuid].segments.push(e);
    });

  const hourMarks = [];
  for (let h = Math.ceil(axisStart / 2) * 2; h <= axisEnd; h += 2) {
    hourMarks.push(h);
  }

  return (
    <div className="mb-4">
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex">
        {/* Task labels */}
        <div className="w-64 flex-none border-r border-slate-100">
          {rows.map((r) => (
            <div
              key={r.task.uuid}
              className="h-7 flex items-center px-3 text-[11px] font-semibold text-slate-600 truncate border-b border-slate-50 last:border-b-0"
            >
              <span className="truncate">{r.task.name}</span>
            </div>
          ))}
        </div>
        {/* Timeline */}
        <div className="flex-1 relative bg-slate-50/40">
          {/* elapsed part of the day */}
          <div
            className="absolute inset-y-0 left-0 bg-slate-100/70"
            style={{ width: `${pct(nowQh)}%` }}
          />
          {hourMarks.map((h) => (
            <span
              key={h}
              className="absolute top-0 bottom-0 border-l border-slate-100"
              style={{ left: `${pct(h)}%` }}
            />
          ))}
          {rows.map((r) => (
            <div
              key={r.task.uuid}
              className="relative h-7 border-b border-slate-50 last:border-b-0"
            >
              {r.segments.map((e) => {
                const isAnytime = e.lo == null;
                const left = isAnytime ? 0 : pct(e.lo);
                const width = isAnytime
                  ? 100
                  : Math.max(pct(e.hi) - pct(e.lo) - 0.15, 0.7);
                return (
                  <Tooltip
                    key={e.key}
                    title={`${e.task.name}${e.slotLabel ? ` · ${e.slotLabel}` : ""} · ${BAR_STATES[e.state].label} · ${e.whenText}`}
                  >
                    <button
                      onClick={() => onBarClick(e)}
                      className={`absolute top-1/2 -translate-y-1/2 h-3.5 rounded ${
                        BAR_STATES[e.state].cls
                      } transition-colors`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          ))}
          {/* now line across every row */}
          <span
            className="absolute top-0 bottom-0 w-0.5 bg-violet-800 ring-1 ring-white z-10"
            style={{ left: `${pct(nowQh)}%` }}
          />
        </div>
      </div>
      {/* Indent by the label-column width so the axis labels sit under the
          timeline itself, not the task names. */}
      <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1 tabular-nums ml-64">
        <span>{formatQhnum(axisStart)}</span>
        <span className="flex items-center gap-3">
          {LEGEND.map((key) => (
            <LegendSwatch key={key} cls={BAR_STATES[key].cls}>
              {BAR_STATES[key].label}
            </LegendSwatch>
          ))}
        </span>
        <span>{formatQhnum(axisEnd)}</span>
      </div>
    </div>
  );
}

function LegendSwatch({ cls, children }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-4 h-2 rounded-sm ${cls}`} />
      {children}
    </span>
  );
}

